import { Color } from "@cesium/engine";
import iro from "@jaames/iro";
import { DitapAlert, DitapMobileSelectPopup, Utils } from "../../..";
import PointEntity from "../edit/PointEntity";
import DitapPopup from "./DitapPopup";

/**
 * PointEntity 스타일 변경 팝업 클래스.</br>
 * A pop-up class for changing the style of a PointEntity.
 * @extends DitapPopup
 *
 * @alias PopupStylePoint
 * @constructor
 *
 * @param {PointEntity} pointEntity 사용할 PopupStylePoint 인스턴스.</br>The instance of PopupStylePoint to change style.
 *
 * @example
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const destination = Ditap.Cartesian3.fromDegrees(
 *   126.97743703843602,
 *   37.5666992934335,
 *    400
 * );
 *
 * // 카메라 이동
 * viewer.scene.camera.flyTo({
 *  destination: destination,
 * });
 *
 * // PointEntity 좌표
 * const position = new Ditap.Cartesian3.fromDegrees(
 *   126.97743703843602,
 *   37.5666992934335
 * );
 *
 * // PointEntity 인스턴스 생성
 * const pointEntity = new Ditap.PointEntity(viewer, position);
 *
 * const popup = new Ditap.PopupStylePoint(pointEntity);
 *
 *
 */
class PopupStylePoint extends DitapPopup {
  constructor(pointEntity, ditapPaneManager) {
    // 부모
    const parent = pointEntity?.viewer?.container;
    super(parent, ditapPaneManager);

    // ENTITY
    if (pointEntity instanceof PointEntity) {
      this.pointEntity = pointEntity;
    } else {
      console.error("Type doesn't match");
    }

    // 모바일일 경우 css 추가
    this.wrapper.classList.add("ditap-popup-mobile");
    this.wrapper.classList.add("ditap-style-change-popup");

    // 스타일 박스 생성
    const styleBox = this.createStyleBox();

    // 헤더 추가
    this.prependHeader(styleBox["header"]["title"]);

    // 컨텐츠 추가
    this.appendSubTitle(styleBox["content"]["subTitle"]);
    this.appendContent(styleBox["content"]["styleList"]);

    // 푸터 추가
    this.appendFooter(styleBox["footer"]["btnWrap"]);

    // 노드 세팅
    this.cancleBtn = this.wrapper.getElementsByClassName(
      "js-ditap-style-cancle-btn"
    )[0];
    this.okBtn = this.wrapper.getElementsByClassName(
      "js-ditap-style-ok-btn"
    )[0];
    this.typeSelectDiv = this.wrapper.getElementsByClassName(
      "js-ditap-style-select"
    )[0];
    this.iconSelectDiv = this.wrapper.getElementsByClassName(
      "js-ditap-style-select"
    )[1];
    this.typeSelectInput = this.wrapper.getElementsByClassName(
      "js-ditap-style-type"
    )[0];
    this.typeSelectBtn = this.wrapper.getElementsByClassName(
      "js-ditap-style-type-btn"
    )[0];
    this.typeOptionDiv = this.wrapper.getElementsByClassName(
      "js-ditap-style-type-opt"
    )[0];
    this.circleType = this.wrapper.getElementsByClassName(
      "js-ditap-style-type-opt-circle-btn"
    )[0];
    this.iconType = this.wrapper.getElementsByClassName(
      "js-ditap-style-type-opt-icon-btn"
    )[0];
    this.diameterInput = this.wrapper.getElementsByClassName(
      "js-ditap-style-diameter"
    )[0];
    this.addDiameterBtn = this.wrapper.getElementsByClassName(
      "js-ditap-style-add-diameter"
    )[0];
    this.subtractDiameterBtn = this.wrapper.getElementsByClassName(
      "js-ditap-style-subtract-diameter"
    )[0];
    this.diameterBox = this.wrapper.getElementsByClassName(
      "js-ditap-style-diameter-box"
    )[0];
    this.colorBox = this.wrapper.getElementsByClassName(
      "js-ditap-style-color-box"
    )[0];
    this.iconBox = this.wrapper.getElementsByClassName(
      "js-ditap-style-icon-box"
    )[0];
    this.colorBtn = this.wrapper.getElementsByClassName(
      "js-ditap-style-color-btn"
    )[0];
    this.colorInput = this.wrapper.getElementsByClassName(
      "js-ditap-style-color"
    )[0];
    this.colorPickBox = this.wrapper.getElementsByClassName(
      "js-ditap-style-color-pickBox"
    )[0];
    this.colorPickerDiv = this.wrapper.getElementsByClassName(
      "js-ditap-style-color-picker"
    )[0];
    this.colorPickerOpenBtn = this.wrapper.getElementsByClassName(
      "js-ditap-color-open-btn"
    )[0];
    this.colorLists = this.wrapper
      .getElementsByClassName("js-ditap-style-color-list")[0]
      .getElementsByTagName("button");
    this.iconOptionDiv = this.wrapper.getElementsByClassName(
      "js-ditap-style-icon-opt"
    )[0];
    this.iconOptionLis = this.iconOptionDiv.getElementsByClassName(
      "js-ditap-style-icon-opt-li"
    );
    this.iconSelectInput = this.wrapper.getElementsByClassName(
      "js-ditap-style-icon"
    )[0];
    this.iconSelectBtn = this.wrapper.getElementsByClassName(
      "js-ditap-style-icon-btn"
    )[0];

    // 스타일 색상 선택 박스 생성
    const colorPicker = this.createColorPicker(parent);
    this.colorPicker = colorPicker;

    this.createMobilePopup(parent);

    // 팝업 버튼 이벤트 추가
    this.createStyleEvent();

    // 기존 스타일 적용
    this.setPopupStylePoint(pointEntity);

    // 팝업 오픈
    this.open();

    this.changeCloseBtn();
    // 드래그 기능 적용
    this.isMobile = Utils.isBodyWidthLessThan600();
    if (!this.isMobile) {
      this.enableDrag();
      this.enableTouch();
    }

    // 경고창 생성
    this.colorAlert = new DitapAlert({
      type: "warning",
      title: "경고",
      content1: "올바른 색상값을 입력해주세요. ",
      content2: "색상코드는 Hex코드로 형식으로 입력가능합니다.(ex: #FFFFFF)",
      buttons: "ONLYOK",
      open: false,
      callback: () => {},
    });

    this.diameterAlert = new DitapAlert({
      type: "warning",
      title: "경고",
      content1: "올바른 지름값을 입력해주세요.",
      content2: "지름은 1이상의 숫자로 입력가능합니다.",
      buttons: "ONLYOK",
      open: false,
      callback: (alert) => {
        alert.destroy();
      },
    });

    if (this.btnWrap) {
      const fullBtn = document.createElement("button");
      fullBtn.classList.add("ditap-popup-full-btn", "ditap-custom-button");

      this.btnWrap.prepend(fullBtn);
      this.fullBtn = this.btnWrap;

      this.fullBtn.addEventListener(
        "click",
        (this.toggleExpandPopup = () => {
          if (this.wrapper.classList.contains("expanded")) {
            this.wrapper.classList.remove("expanded");
          } else {
            this.wrapper.classList.add("expanded");
          }
        })
      );
    }
  }

  createMobilePopup(parent) {
    // 1. 스타일 팝업
    this.mobileTypeSelectPopup = new DitapMobileSelectPopup(parent);
    this.mobileTypeSelectPopup.addTitle("스타일");
    this.mobileTypeSelectPopup.closeCallback = () => {
      this.typeOptionDiv.classList.add("hide");
      this.typeSelectDiv.classList.remove("on");
    };

    // 3. 아이콘 팝업
    this.mobileIconSelectPopup = new DitapMobileSelectPopup(parent);
    this.mobileIconSelectPopup.addTitle("스타일");
    this.mobileIconSelectPopup.closeCallback = () => {
      this.iconOptionDiv.classList.add("hide");
      this.iconSelectInput.classList.remove("on");
    };

    // 2. 색상 팝업
    this.mobileColorPickerPopup = new DitapMobileSelectPopup(parent);
    this.mobileColorPickerPopup.addTitle("색상");

    // 버튼 추가
    const btnWrap = document.createElement("div");
    const okBtn = document.createElement("button");
    const cancleBtn = document.createElement("button");
    okBtn.textContent = "확인";
    cancleBtn.textContent = "취소";
    okBtn.classList.add("ditap-popup-btn", "gold");
    btnWrap.classList.add("ditap-btn-wrap");
    okBtn.classList.add("ditap-popup-btn", "gold");
    cancleBtn.classList.add("ditap-popup-btn");
    btnWrap.appendChild(cancleBtn);
    btnWrap.appendChild(okBtn);
    this.mobileColorPickerPopup.appendFooter(btnWrap);

    okBtn.addEventListener("click", () => {
      const color = this.mobileColorInput.value;
      if (color.length === 7 && this.checkAvailableColor(color)) {
        this.mobileColorPickerPopup.close();
      } else {
        this.colorAlert.open();
        this.mobileColorInput.value = "#FFFFFF";
        this.colorPicker.color.red = 255;
        this.colorPicker.color.green = 255;
        this.colorPicker.color.blue = 255;
      }
    });

    cancleBtn.addEventListener("click", () => {
      this.mobileColorPickerPopup.close();
      this.colorPickBox.classList.add("hide");
      this.changeColorPickerByInput(this.originInputValue, this.colorInput);
    });

    // 색상 박스 생성
    const div = document.createElement("div");
    const inp = document.createElement("input");
    inp.classList.add("ditap-style-inp");
    inp.type = "text";
    inp.maxLength = "7";
    div.append(inp);
    this.mobileColorPickerPopup.appendContent(div);

    this.mobileColorInput = inp;
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   */
  createResizeEvent() {
    super.createResizeEvent();

    window.addEventListener(
      "resize",
      (this.resizeEvent = () => {
        const currentIsMobile = Utils.isBodyWidthLessThan600();

        if (this.isMobile !== currentIsMobile) {
          this.changeCloseBtn();
          this.isMobile = currentIsMobile;
        }

        // 컬러픽커 리사이즈 이벤트
        const width = Utils.getColorPickerWidth();
        this.colorPicker.resize(width);

        this.setMobilePopup();
      })
    );
  }

  setMobilePopup() {
    const isMobile = Utils.isBodyWidthLessThan600();
    if (isMobile && this.colorBox.contains(this.colorPickBox)) {
      // 컬러픽커 팝업 이동
      this.originInputValue = this.colorInput.value;
    }

    this.moveMobileContentPopup(
      this.colorBox,
      this.mobileColorPickerPopup,
      this.colorPickBox
    );
    this.moveMobileContentPopup(
      this.typeSelectDiv,
      this.mobileTypeSelectPopup,
      this.typeOptionDiv
    );
    this.moveMobileContentPopup(
      this.iconSelectDiv,
      this.mobileIconSelectPopup,
      this.iconOptionDiv
    );
  }

  moveMobileContentPopup(container, popup, content) {
    const isMobile = Utils.isBodyWidthLessThan600();
    if (isMobile && container.contains(content)) {
      container.removeChild(content);
      popup.appendContent(content);
    } else if (!isMobile && popup.content.contains(content)) {
      popup.content.removeChild(content);
      container.appendChild(content);
    }
  }

  removeResizeEvent() {
    window.removeEventListener("resize", this.resizeEvent);
  }

  /**
   * 스타일 수정 할 포인트 엔티티.</br>
   * Point entity to modify the style.
   * @type {PointEntity}
   * @readonly
   */
  set pointEntity(pointEntity) {
    this._pointEntity = pointEntity;
  }
  get pointEntity() {
    return this._pointEntity;
  }

  /*
   * 선택 색상의 컬러 픽커.</br>
   * The color picker to select color.
   * @type {object}
   * @see {@link https://iro.js.org/colorPicker_api.html}
   */
  set colorPicker(colorPicker) {
    this._colorPicker = colorPicker;
  }
  get colorPicker() {
    return this._colorPicker;
  }

  /**
   * 위젯 파괴. 영구적으로 제거하는 경우 호출.</br>
   * Destroys the widget.  Should be called if permanently
    removing the widget from layout.
   */
  destroy() {
    if (this.fullBtn instanceof Element) {
      this.fullBtn.removeEventListener("click", this.toggleExpandPopup);
    }

    this.removeResizeEvent();
    super.destroy();
    this.colorAlert.destroy();
    this.diameterAlert.destroy();
    this.mobileColorPickerPopup.destroy();
    this.mobileTypeSelectPopup.destroy();
  }

  toggleTypeSelectInput() {
    if (this.typeOptionDiv.classList.contains("hide")) {
      this.typeOptionDiv.classList.remove("hide");
      this.typeSelectDiv.classList.add("on");
      this.mobileTypeSelectPopup.open();
      this.setMobilePopup();
    } else {
      this.typeOptionDiv.classList.add("hide");
      this.typeSelectDiv.classList.remove("on");
      this.mobileTypeSelectPopup.close();
    }
  }
  /*
   * 버튼 이벤트 생성.</br>
   * Create a button event.
   */
  createStyleEvent() {
    // 취소 버튼
    this.cancleBtn.addEventListener("click", () => {
      this.close();
    });

    this.typeSelectInput.addEventListener("click", () => {
      this.typeSelectBtn.click();
    });

    // 타입 - 셀렉트 박스 open/close
    this.typeSelectBtn.addEventListener("click", () => {
      this.toggleTypeSelectInput();
    });

    // 타입 - 원형 옵션 클릭
    this.circleType.addEventListener("click", () => {
      this.openCircleType();
      this.setMobilePopup();

      this.circleType.querySelector(".ditap-radio-btn").classList.add("on");
      this.iconType.querySelector(".ditap-radio-btn").classList.remove("on");
    });

    // 타입 - 아이콘 옵션 클릭
    this.iconType.addEventListener("click", () => {
      this.openIconType();
      this.setMobilePopup();

      this.iconType.querySelector(".ditap-radio-btn").classList.add("on");
      this.circleType.querySelector(".ditap-radio-btn").classList.remove("on");
    });

    this.iconSelectInput.addEventListener("click", () => {
      this.iconSelectBtn.click();
    });

    // 아이콘 - 셀렉트 박스 open/close
    this.iconSelectBtn.addEventListener("click", () => {
      if (this.iconOptionDiv.classList.contains("hide")) {
        this.iconOptionDiv.classList.remove("hide");
        this.mobileIconSelectPopup.open();
        this.setMobilePopup();
      } else {
        this.iconOptionDiv.classList.add("hide");
        this.mobileIconSelectPopup.close();
      }
    });

    // 아이콘 - 아이콘 옵션 클릭
    Array.from(this.iconOptionLis).forEach((optionLi) => {
      optionLi.addEventListener("click", () => {
        this.mobileTypeSelectPopup.close();
        this.iconSelectInput.textContent = optionLi.getElementsByTagName(
          "p"
        )[1].textContent;
        this.iconOptionDiv.classList.add("hide");

        for (const allLi of this.iconOptionLis) {
          allLi.querySelector(".ditap-radio-btn").classList.remove("on");
        }

        optionLi.querySelector(".ditap-radio-btn").classList.add("on");
        this.setMobilePopup();
        this.mobileIconSelectPopup.close();
      });
    });

    // 지름 - 지름 크기 증가 버튼 클릭
    this.addDiameterBtn.addEventListener("click", () => {
      const diameter = Number(this.diameterInput.value);
      this.diameterInput.value = diameter + 1;
    });

    // 지름 - 지름 크기 감소 버튼 클릭
    this.subtractDiameterBtn.addEventListener("click", () => {
      const diameter = Number(this.diameterInput.value);
      if (diameter - 1 > 0) {
        this.diameterInput.value = diameter - 1;
      }
    });

    // 지름 - 인풋 박스 변경시, 지름값 변경
    this.diameterInput.addEventListener("keyup", (e) => {
      const diameter = e.target.value;
      this.diameterInput.value = diameter;
    });

    // 색상 - 색상 버튼 클릭
    this.colorPickerOpenBtn.addEventListener("click", () => {
      // 색상 박스 생성
      if (!this.mobileColorPickerPopup.isOpen) {
        this.colorPickBox.classList.remove("hide");
        this.mobileColorPickerPopup.open();
        this.originInputValue = this.colorInput.value;
        this.setMobilePopup();
      } else {
        this.colorPickBox.classList.add("hide");
        this.mobileColorPickerPopup.close();
        this.setMobilePopup();
      }
    });

    // 색상 - 컬러픽커에서 색상변경시, 색생값 변경
    this.colorPicker.on("color:change", (color) => {
      const hex = Color.fromBytes(
        color.red,
        color.green,
        color.blue
      ).toCssHexString();

      // 색상 인풋박스 값 변경
      this.colorInput.value = hex.toUpperCase();
      this.mobileColorInput.value = hex.toUpperCase();
    });

    // 색상 - 인풋 박스 변경시, 색상값 변경
    this.colorInput.addEventListener("keyup", (e) => {
      const color = e.target.value.toUpperCase();
      this.changeColorPickerByInput(color, this.colorInput);
    });

    // 색상 - 인풋 박스 변경시, 색상값 변경
    this.mobileColorInput.addEventListener("keyup", (e) => {
      const color = e.target.value.toUpperCase();
      this.changeColorPickerByInput(color, this.mobileColorInput);
    });

    // 색상 - 리스트 클릭 시, 색상값 변경
    Array.from(this.colorLists).forEach((colorLi) => {
      colorLi.addEventListener("click", () => {
        const rgbColor = window.getComputedStyle(colorLi).backgroundColor;

        const byteColor = Color.fromCssColorString(rgbColor).toBytes();
        this.colorPicker.color.red = byteColor[0];
        this.colorPicker.color.green = byteColor[1];
        this.colorPicker.color.blue = byteColor[2];
      });
    });

    // 확인 - 엔티티 적용
    this.okBtn.addEventListener("click", () => {
      // 엔티티 변경 타입이 원형인 경우
      if (this.typeSelectInput.textContent === this.circleType.textContent) {
        // 지름 적용
        const diameter = this.diameterInput.value;

        // 색상 적용
        const color = this.colorInput.value;

        if (Number(diameter) > 0 && diameter) {
          if (this.checkAvailableColor(color)) {
            const ditapColor = Color.fromCssColorString(color);
            ditapColor.alpha = this.colorPicker.color.alpha;

            this.pointEntity.setPointShow(true);
            this.pointEntity.setBillboardShow(false);
            this.pointEntity.setPointOutLineColor(ditapColor);
            this.pointEntity.setPointPixelSize(diameter - 3);
            this.close();
          } else {
            this.colorAlert.open();
          }
        } else {
          this.diameterAlert.open();
        }
      } else if (
        this.typeSelectInput.textContent === this.iconType.textContent
      ) {
        // 엔티티 변경 타입이 아이콘인 경우
        const icon = this.iconSelectInput.textContent;
        switch (icon) {
          case "깃발":
            this.pointEntity.setBillboard("flag");
            break;
          case "핀":
            this.pointEntity.setBillboard("pin");
            break;
          case "말풍선":
            this.pointEntity.setBillboard("balloon");
            break;
          default:
            break;
        }
        this.close();
      }
    });
  }

  changeColorPickerByInput(color, colorInput) {
    if (color.length === 7) {
      if (this.checkAvailableColor(color)) {
        // 16진수 -> 10진수
        const byteColor = Color.fromCssColorString(color).toBytes();
        this.colorPicker.color.red = byteColor[0];
        this.colorPicker.color.green = byteColor[1];
        this.colorPicker.color.blue = byteColor[2];
      } else {
        this.colorAlert.open();
        colorInput.value = "#FFFFFF";
        this.colorPicker.color.red = 255;
        this.colorPicker.color.green = 255;
        this.colorPicker.color.blue = 255;
      }
    }
  }

  /**
   * 팝업 닫기.</br>
   * Close PopupStylePoint.
   */
  close() {
    super.close();
    this.mobileColorPickerPopup.close();
    this.mobileColorPickerPopup.destroy();
    this.mobileTypeSelectPopup.close();
    this.mobileTypeSelectPopup.destroy();
    this.mobileIconSelectPopup.close();
    this.mobileIconSelectPopup.destroy();
  }

  /*
   * 색상 변경 팝업 요소 생성.</br>
   * Create PopupStylePoint Element
   * @returns {object} PopupStylePoint 요소.</br>The PopupStylePoint Element.
   */
  createStyleBox() {
    this.wrapper.classList.add(
      "ditap-mobile-bottom",
      "header-top",
      "draggable"
    );
    // # 헤더
    const title = document.createElement("h2");
    title.classList.add("ditap-popup-title");
    title.innerText = "스타일";

    const subTitle = document.createElement("p");
    subTitle.classList.add("ditap-popup-subtitle");
    subTitle.innerText = "아래의 스타일을 선택해주세요.";
    const styleList = document.createElement("div");
    styleList.classList.add("ditap-popup-style-list");
    styleList.innerHTML = `
    <div class="ditap-style-item">
      <p class="ditap-style-title">스타일</p>
      <div class="ditap-style-inp-wrap ditap-style-select js-ditap-style-select">
        <div class="ditap-style-inp js-ditap-style-type">원형</div>
        <button class="ditap-dropbox-img ditap-custom-button js-ditap-style-type-btn"></button>
        <div class="ditap-style-opt js-ditap-style-type-opt hide">
          <ul class="ditap-style-opt-list">
          <button class="ditap-style-opt-title js-ditap-style-type-opt-circle-btn ditap-custom-button">원형<div class="ditap-radio-btn on"></div></button>
          <button class="ditap-style-opt-title js-ditap-style-type-opt-icon-btn ditap-custom-button">아이콘<div class="ditap-radio-btn"></div></button>
          </ul>
        </div>
      </div>
    </div>  
    <div class="ditap-style-item ditap-line js-ditap-style-diameter-box">
      <p class="ditap-style-title">지름</p>
      <div class="ditap-style-inp-wrap">
        <label for="diameter0">
          <input id="diameter0" type="number" class="ditap-style-inp js-ditap-style-diameter" value="11"/>
        </label>
        <button class="ditap-add-btn js-ditap js-ditap-style-add-diameter ditap-custom-button"></button>
        <button class="ditap-subtract-btn js-ditap-style-subtract-diameter ditap-custom-button"></button>
      </div>
    </div>
    <div class="ditap-style-color-wrap js-ditap-style-color-box">
      <div class="ditap-style-item js-ditap-style-color-input-box">
        <p class="ditap-style-title">색상</p>
        <div class="ditap-style-inp-wrap js-ditap-style-color-btn">
          <input type="text" class="ditap-style-inp js-ditap-style-color" maxlength="7" value="#FFFFFF"></input>
          <button class="ditap-palette-img js-ditap-color-open-btn ditap-custom-button"></button>
        </div>
      </div>
      <div class="ditap-style-color  js-ditap-style-color-pickBox hide">
        <div class="js-ditap-style-color-picker"> </div>
        <div>
            <ul class="ditap-style-color-list js-ditap-style-color-list">
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #6127FF;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #0067F4;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #00D0FF;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #1BC47D;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #6FCF97;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #FF2C60;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #FF5A83;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #000000;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #1C1C1C;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #333333;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #828282;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #BDBDBD;"></button>
            </ul>
        </div>
      </div>
    </div>
    <div class="ditap-style-item  js-ditap-style-icon-box hide">
          <p class="ditap-style-title">아이콘</p>
          <div class="ditap-style-inp-wrap ditap-style-select js-ditap-style-select">
            <div class="ditap-style-inp js-ditap-style-icon">깃발</div>
            <button class="ditap-dropbox-img ditap-custom-button  js-ditap-style-icon-btn"></button>
            <div class="ditap-style-opt js-ditap-style-icon-opt hide">
              <ul class="ditap-style-opt-list">
                <button class="ditap-style-opt-title ditap-opt-flag ditap-custom-button js-ditap-style-icon-opt-li">
                  <p class="ditap-style-opt-img"></p>
                  <p class="ditap-padding-reset">깃발</p>
                  <div class="ditap-radio-btn on"></div>
                </button>
                <button class="ditap-style-opt-title ditap-opt-pin ditap-custom-button js-ditap-style-icon-opt-li">
                  <p class="ditap-style-opt-img"></p>
                  <p class="ditap-padding-reset">핀</p>
                  <div class="ditap-radio-btn"></div>
                </button>
                <button class="ditap-style-opt-title ditap-opt-speechbubble ditap-custom-button js-ditap-style-icon-opt-li">
                  <p class="ditap-style-opt-img"></p>
                  <p class="ditap-padding-reset">말풍선</p>
                  <div class="ditap-radio-btn"></div>
                </button>
              </ul>
            </div>
          </div>
        </div>
    `;

    const btnWrap = document.createElement("div");
    btnWrap.classList.add("ditap-btn-wrap");
    btnWrap.innerHTML = `<button class="ditap-popup-btn js-ditap-style-cancle-btn">취소</button><button class="ditap-popup-btn js-ditap-style-ok-btn gold">확인</button>`;

    return {
      header: {
        title: title,
      },
      content: {
        subTitle: subTitle,
        styleList: styleList,
      },
      footer: {
        btnWrap: btnWrap,
      },
    };
  }

  /*
   * 컬러픽커 생성.</br>
   * Create ColorPicker to change the style of a PointEntity.
   * @returns {iro.ColorPicker} 생성된 컬러픽커.</br>The ColorPicker.
   */
  createColorPicker() {
    // 색상 - 컬러픽커 생성
    const width = Utils.getColorPickerWidth();

    const colorPicker = new iro.ColorPicker(this.colorPickerDiv, {
      width: width,
      boxHeight: 180,
      color: "rgb(0, 0, 0)",
      layout: [
        {
          component: iro.ui.Box,
        },
        {
          component: iro.ui.Slider,
          options: {
            sliderType: "hue",
            sliderSize: 10,
          },
        },
        {
          component: iro.ui.Slider,
          options: {
            sliderType: "alpha",
            sliderSize: 10,
          },
        },
      ],
    });

    return colorPicker;
  }

  /*
   * 원형 유형 모드의 팝업 열기.</br>
   * Opens a PopupStylePoint of circle type mode.
   */
  openCircleType() {
    // 원형 타입 활성화
    // 지름, 색상 div 활성화, icon div 비활성화
    this.diameterBox.classList.remove("hide");
    this.colorBox.classList.remove("hide");
    this.iconBox.classList.add("hide");

    this.typeOptionDiv.classList.add("hide");
    this.typeSelectDiv.classList.remove("on");
    this.typeSelectInput.textContent = this.circleType.textContent;
    this.mobileTypeSelectPopup.close();
  }

  /*
   * 아이콘 유형 모드의 팝업 열기.</br>
   * Opens a PopupStylePoint of icon type mode.
   */
  openIconType() {
    // 아이콘 타입 활성화
    // 지름, 색상 Box 활성화, icon Box 비활성화
    this.diameterBox.classList.add("hide");
    this.colorBox.classList.add("hide");
    this.iconBox.classList.remove("hide");

    this.typeOptionDiv.classList.add("hide");
    this.typeSelectDiv.classList.remove("on");
    this.typeSelectInput.textContent = this.iconType.textContent;
    this.mobileTypeSelectPopup.close();
  }

  /*
   * 기존 엔티티 스타일 적용.</br>
   * Apply the style of the existing Entity.
   * @param {PointEntity} pointEntity 스타일 적용할 PointEntity 인스턴스.</br> The instance of PointEntity to apply
   */
  setPopupStylePoint(pointEntity) {
    // 팝업이 열릴 때, 기존 엔티티의 스타일을 팝업에 적용시킨다.
    if (pointEntity.pointEntity) {
      const isPoint = pointEntity.pointEntity.point.show.getValue();
      const isBillboard = pointEntity.pointEntity.billboard.show.getValue();

      if (isPoint) {
        // point 활성화
        this.openCircleType();

        // INPUT 지름 적용
        this.diameterInput.value = pointEntity.pointEntity.point.pixelSize + 3;

        // INPUT 색상 적용
        const color = pointEntity.pointEntity.point.outlineColor.getValue();
        this.colorPicker.color.red = color.red * 255;
        this.colorPicker.color.green = color.green * 255;
        this.colorPicker.color.blue = color.blue * 255;
        this.colorPicker.color.alpha = color.alpha;

        this.setMobilePopup();
      } else if (isBillboard) {
        //icon 활성화
        this.openIconType();
        // INPUT 아이콘 변경
        if (pointEntity.getBillboardType() === "flag") {
          this.iconSelectInput.textContent = "깃발";
        } else if (pointEntity.getBillboardType() === "pin") {
          this.iconSelectInput.textContent = "핀";
        } else if (pointEntity.getBillboardType() === "balloon") {
          this.iconSelectInput.textContent = "말풍선";
        }
      }
    }
  }

  /*
   * HEX 색상 코드를 사용 여부 조회.</br>
   * Check if it is a usable HEX color code.
   * @param {string} color 확인할 색상 코드.</br>Color code to check(ex: "#000000").
   * @returns {boolean} 사용 가능 여부.</br>Usable Status.
   */
  checkAvailableColor(color) {
    if (Color.fromCssColorString(color)) {
      return true;
    }

    return false;
  }
}
export default PopupStylePoint;
